import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import RoutesDiagram from '../../sections/day-trip-listing/RoutesDiagram'
import DayTripGrid from '../../sections/day-trip-listing/DayTripGrid'
import {
  apiGetDayTrips,
  apiGetDayTripById,
  type DayTrip,
} from '../../api/dayTrips/dayTripApi'
import LocationDropdown from '../../components/LocationDropdown'
import DateTimePicker, {
  type DateTimeValue,
  formatDateLabel,
  formatTimeLabel,
} from '../../components/DateTimePicker'
import { bookingService } from '../../api/services/bookingService'
import type { Vehicle } from '../../types/booking'

export default function DayTripListing() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const tripId = searchParams.get('id')

  const [trips, setTrips] = useState<DayTrip[]>([])
  const [selectedTrip, setSelectedTrip] = useState<DayTrip | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)

  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [vehiclesLoading, setVehiclesLoading] = useState(true)

  const [adultsCount, setAdultsCount] = useState(2)
  const [childrenCount, setChildrenCount] = useState(0)
  const travelersCount = adultsCount + childrenCount

  const vehiclesRef = useRef<HTMLDivElement>(null)

  const [departure, setDeparture] = useState<DateTimeValue>({
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
  })

  const [selectedFrom, setSelectedFrom] = useState<{
    title: string
    address: string
  } | null>(null)

  const [pickupLocationError, setPickupLocationError] = useState(false)
  const [isFromOpen, setIsFromOpen] = useState(false)
  const [isDepartureOpen, setIsDepartureOpen] = useState(false)
  const [isBannerDepartureOpen, setIsBannerDepartureOpen] = useState(false)
  const [showTravelersDropdown, setShowTravelersDropdown] = useState(false)
  const [showCars, setShowCars] = useState(false)
  const [activeInfoTab, setActiveInfoTab] = useState<
    'overview' | 'itinerary'
  >('overview')

  /*
   * Filter vehicles according to their minimum booking hours.
   *
   * Each vehicle can have a different minimum booking notice.
   * Example:
   *   minimumBookingHours = 2
   *   pickup must therefore be at least 2 hours from now.
   *
   * Invalid/missing date-time values are left visible rather than
   * incorrectly hiding the fleet.
   */
  const availableVehicles = vehicles.filter((vehicle) => {
    if (!departure.date || !departure.time) {
      return true
    }

    const [year, month, day] = departure.date.split('-').map(Number)
    const [hours, minutes] = departure.time.split(':').map(Number)

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return true
    }

    const pickupDateTime = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      0,
      0,
    )

    const minimumBookingHours = Math.max(
      1,
      Number(vehicle.minimumBookingHours ?? 1),
    )

    const earliestPickup = new Date(
      Date.now() + minimumBookingHours * 60 * 60 * 1000,
    )

    return pickupDateTime.getTime() >= earliestPickup.getTime()
  })

  const validateBooking = () => {
    if (!selectedFrom) {
      setPickupLocationError(true)
      toast.error('Please provide a pickup location.')
      return false
    }

    setPickupLocationError(false)
    return true
  }

  /*
   * Calculate dynamic group pricing based on the database base price.
   *
   * 1-3 travelers  -> base price
   * 4-6 travelers  -> base price + 120
   * 7-8 travelers  -> base price + 240
   */
  const basePrice = selectedTrip ? selectedTrip.price : 480

  let calculatedPrice = basePrice
  let groupText = 'For a group of 1-3 travelers'

  if (travelersCount <= 3) {
    calculatedPrice = basePrice
    groupText = 'For a group of 1-3 travelers'
  } else if (travelersCount <= 6) {
    calculatedPrice = basePrice + 120
    groupText = 'For a group of 4-6 travelers'
  } else {
    calculatedPrice = basePrice + 240
    groupText = 'For a group of 7-8 travelers'
  }

  const handleReserveCar = (vehicleId: string) => {
    if (!validateBooking()) return

    const params = new URLSearchParams({
      serviceType: 'daytrips',
      skipStops: 'true',
      vehicle: vehicleId,
      adults: adultsCount.toString(),
      children: childrenCount.toString(),
      date: departure.date,
      time: departure.time,
    })

    if (selectedFrom) {
      params.set('fromTitle', selectedFrom.title)
      params.set('fromAddress', selectedFrom.address)
    }

    if (selectedTrip) {
      params.set('toTitle', selectedTrip.title)
      params.set('serviceType', 'daytrips')
      params.set('serviceId', selectedTrip.id)

      /*
       * Keep the service price separate from the vehicle price.
       * The booking flow can then combine these values correctly.
       */
      params.set('servicePrice', String(calculatedPrice))
      params.set('serviceTitle', selectedTrip.title)

      /*
       * Preserve A's service duration parameter.
       * This was accidentally removed in B.
       */
      if (selectedTrip.duration) {
        params.set('serviceDuration', selectedTrip.duration)
      }
    }

    navigate(`/booking?${params.toString()}`)
  }

  // Fetch all vehicles
  useEffect(() => {
    const loadVehicles = async () => {
      setVehiclesLoading(true)

      try {
        const data = await bookingService.fetchVehicles()
        setVehicles(data)
      } catch (error) {
        console.error('Failed to load vehicles', error)
      } finally {
        setVehiclesLoading(false)
      }
    }

    loadVehicles()
  }, [])

  // Fetch all trips for listing
  useEffect(() => {
    async function loadTrips() {
      setLoading(true)

      const res = await apiGetDayTrips()

      if (res.success && res.data) {
        const activeTrips = res.data.filter(
          (trip) => trip.active === true,
        )

        setTrips(activeTrips)
      }

      setLoading(false)
    }

    loadTrips()
  }, [])

  // Fetch details of selected trip
  useEffect(() => {
    const selectedId = tripId

    if (selectedId) {
      async function loadDetail(id: string) {
        setDetailLoading(true)

        const res = await apiGetDayTripById(id)

        if (res.success && res.data && res.data.active === true) {
          setSelectedTrip(res.data)
        } else {
          setSelectedTrip(null)
        }

        setDetailLoading(false)
      }

      loadDetail(selectedId)
    } else {
      setSelectedTrip(null)
    }
  }, [tripId])

  // Scroll to top on selections
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [selectedTrip])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)

    if (isNaN(date.getTime())) return dateString

    const standardWeekdays = [
      'Sun',
      'Mon',
      'Tue',
      'Wed',
      'Thu',
      'Fri',
      'Sat',
    ]

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]

    return `${standardWeekdays[date.getDay()]} ${date.getDate()} ${
      months[date.getMonth()]
    }, ${date.getFullYear()}`
  }

  const expectations = [
    {
      title: 'Convenient Salzburg Pickup',
      desc: 'Your professional driver will meet you directly at your hotel, apartment, or address in Salzburg, ensuring a smooth and hassle-free departure.',
    },
    {
      title: 'Scenic Alpine Travels',
      desc: 'Sit back and enjoy the incredible landscapes. We travel via highly scenic routes and allow for custom photo stops along the way.',
    },
    {
      title: 'Self-Paced Sightseeing',
      desc: 'Explore the destination entirely at your own pace. Dine at local restaurants, shop, visit historical attractions, and walk through town.',
    },
  ]

  // Setup gallery images
  const images = selectedTrip?.images ?? []

  const mainImage = images[0] ?? ''
  const gridImages = images.slice(1, 5)

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {tripId ? (
        /* ================= DETAIL VIEW ================= */
        <div className="flex flex-col flex-1">
          <div className="relative z-20 border-b border-slate-100 bg-white px-4">
            <Header />
          </div>

          <main className="mx-auto w-full max-w-[1280px] px-4 py-8 sm:px-6 sm:pb-12 sm:pt-4 lg:px-[42px]">
            {detailLoading ? (
              <div className="flex flex-col items-center justify-center py-32">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-brand-green rounded-full animate-spin" />

                <p className="mt-4 font-host text-slate-500 font-semibold text-[15px]">
                  Loading trip details...
                </p>
              </div>
            ) : selectedTrip ? (
              <>
                {/* Back button and Title */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    onClick={() => setSearchParams({})}
                    className="flex items-center gap-1.5 self-start sm:self-auto rounded-full border border-[#E5E5E5] bg-white px-4 py-2 font-host text-sm font-semibold text-[#333333] transition-all hover:bg-slate-50 active:scale-[0.98]"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="text-[#333333]"
                    >
                      <path
                        d="M10 13L5 8L10 3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span>Back</span>
                  </button>

                  <h1 className="font-host text-[22px] sm:text-[28px] font-bold text-[#111111]">
                    Private Day Trip to {selectedTrip.title}
                  </h1>
                </div>

                {/* Photo Gallery Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                  <div className="h-[240px] sm:h-[280px] lg:h-[320px] overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <img
                      src={mainImage}
                      alt={selectedTrip.title}
                      className="w-full h-full object-cover transition-transform hover:scale-[1.01] duration-300"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 h-[240px] sm:h-[280px] lg:h-[320px]">
                    {gridImages.map((imgSrc, idx) => (
                      <div
                        key={idx}
                        className="overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                      >
                        <img
                          src={imgSrc}
                          alt=""
                          className="w-full h-full object-cover hover:scale-105 transition-all duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subtitle */}
                <h2 className="font-host text-[20px] sm:text-[24px] font-bold text-[#111111] mt-6">
                  {selectedTrip.subtitle}
                </h2>

                {/* Metadata Badges row */}
                <div className="flex flex-wrap items-center gap-6 py-3 text-[14px] font-medium text-[#4A4A4A]">
                  <div className="flex items-center gap-2">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-brand-green"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>

                    <span className="font-host text-[14px] font-semibold text-[#444444]">
                      Duration : {selectedTrip.duration || '8–10 Hours'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-brand-green"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>

                    <span className="font-host text-[14px] font-semibold text-[#444444]">
                      Max People: {selectedTrip.maxPeople || 8}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-brand-green"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>

                    <span className="font-host text-[14px] font-semibold text-[#444444]">
                      Start Location :{' '}
                      {selectedTrip.startLocation || 'Salzburg'}
                    </span>
                  </div>
                </div>

                {/* Split Layout columns */}
                <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-10 lg:gap-14 mt-6">
                  {/* Left Column info */}
                  <div>
                    {/* Green booking availability banner bar */}
                    <div className="rounded-[16px] bg-brand-green p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                      <div className="flex items-center gap-3">
                        <span className="font-host text-[18px] sm:text-[20px] font-bold text-white leading-none">
                          Make your booking
                        </span>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Date Selector */}
                        <div className="relative">
                          <button
                            onClick={() =>
                              setIsBannerDepartureOpen(
                                !isBannerDepartureOpen,
                              )
                            }
                            className="flex items-center gap-2.5 rounded-[12px] bg-white px-4 py-2.5 shadow-sm min-w-[200px] transition-opacity hover:opacity-90"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              className="text-black flex-shrink-0"
                            >
                              <rect
                                x="3"
                                y="4"
                                width="18"
                                height="18"
                                rx="2"
                                ry="2"
                              />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>

                            <span className="font-host text-[14px] font-bold text-black select-none">
                              {formatDate(departure.date)}
                            </span>
                          </button>

                          {isBannerDepartureOpen && (
                            <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-[320px]">
                              <DateTimePicker
                                value={departure}
                                allowNow
                                onCancel={() =>
                                  setIsBannerDepartureOpen(false)
                                }
                                onConfirm={(next) => {
                                  setDeparture(next)
                                  setIsBannerDepartureOpen(false)
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            setShowCars(true)

                            setTimeout(() => {
                              vehiclesRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              })
                            }, 100)

                            toast(
                              'Please select a vehicle below to proceed.',
                              {
                                icon: '⬇️',
                              },
                            )
                          }}
                          className="rounded-[12px] bg-black px-6 py-2.5 font-host text-[14px] font-bold text-white hover:bg-slate-900 active:scale-[0.98] transition-all"
                        >
                          Check Availability
                        </button>
                      </div>
                    </div>

                    {/* ================= VEHICLE AVAILABILITY ================= */}
                    {showCars && (
                      <div
                        ref={vehiclesRef}
                        className="mb-8 p-6 rounded-[24px] bg-[#F9F9F9] border border-[#E5E5E5] shadow-sm"
                      >
                        <h3 className="font-host text-[20px] font-bold text-[#111111] mb-4">
                          Select your vehicle
                        </h3>

                        {vehiclesLoading ? (
                          <div className="py-6 text-center">
                            <div className="mx-auto w-8 h-8 border-4 border-slate-200 border-t-brand-green rounded-full animate-spin" />

                            <p className="mt-3 font-host text-[14px] text-slate-500">
                              Loading vehicles...
                            </p>
                          </div>
                        ) : vehicles.length === 0 ? (
                          <div className="py-6 text-center">
                            <p className="font-host text-[14px] text-slate-500">
                              No vehicles available.
                            </p>
                          </div>
                        ) : availableVehicles.length === 0 ? (
                          <div className="py-6 text-center">
                            <p className="font-host text-[14px] text-slate-500">
                              No vehicles are available for the selected
                              pickup time.
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            {(() => {
                              /*
                               * Sort vehicles so those capable of carrying
                               * the selected party appear first.
                               *
                               * Within each group, smaller-capacity vehicles
                               * appear first.
                               */
                              const sortedVehicles = [
                                ...availableVehicles,
                              ].sort((a, b) => {
                                const aFits =
                                  Number(a.passengers ?? 0) >= travelersCount

                                const bFits =
                                  Number(b.passengers ?? 0) >= travelersCount

                                // Keep suitable vehicles first.
                                if (aFits && !bFits) return -1
                                if (!aFits && bFits) return 1

                                // Within the same suitability group,
                                // preserve the backend display_order.
                                return (
                                  Number(a.displayOrder ?? a.display_order ?? 0) -
                                  Number(b.displayOrder ?? b.display_order ?? 0)
                                )
                              })

                              const suitableVehiclesCount =
                                sortedVehicles.filter(
                                  (vehicle) =>
                                    Number(vehicle.passengers ?? 0) >=
                                    travelersCount,
                                ).length

                              return (
                                <>
                                  {suitableVehiclesCount === 0 && (
                                    <div className="rounded-[14px] border border-[#FCD34D] bg-[#FFFBEB] p-4 text-[13px] text-[#92400E]">
                                      <p className="font-semibold">
                                        No fleet available for{' '}
                                        {travelersCount} passengers
                                      </p>

                                      <p className="mt-1 text-[12px] text-[#B45309]">
                                        All available vehicles seat fewer than{' '}
                                        {travelersCount} passengers. Fleets
                                        below are shown faded as they cannot
                                        accommodate your party.
                                      </p>
                                    </div>
                                  )}

                                  {sortedVehicles.map((vehicle, index) => {
                                    /*
                                     * B introduced dayTripPrice.
                                     *
                                     * Keep a fallback to price so older API
                                     * responses do not unexpectedly produce
                                     * a zero vehicle charge.
                                     */
                                    const vehiclePrice = Number(
                                      vehicle.dayTripPrice ??
                                        vehicle.price ??
                                        0,
                                    )

                                    const displayedPrice = Math.round(
                                      Number(calculatedPrice) + vehiclePrice,
                                    )

                                    const isEnoughCapacity =
                                      Number(vehicle.passengers ?? 0) >=
                                      travelersCount

                                    return (
                                      <div
                                        key={vehicle.id}
                                        className="w-full"
                                      >
                                        {index === suitableVehiclesCount &&
                                          suitableVehiclesCount > 0 && (
                                            <div className="my-2.5 flex items-center gap-3 py-1">
                                              <div className="h-px flex-1 bg-[#E0E0E0]" />

                                              <span className="font-poppins text-[10px] font-semibold uppercase tracking-wider text-[#888888] sm:text-[11px]">
                                                Other fleets (
                                                {sortedVehicles.length -
                                                  suitableVehiclesCount}{' '}
                                                seat fewer than{' '}
                                                {travelersCount} passengers)
                                              </span>

                                              <div className="h-px flex-1 bg-[#E0E0E0]" />
                                            </div>
                                          )}

                                        <button
                                          type="button"
                                          disabled={!isEnoughCapacity}
                                          aria-disabled={!isEnoughCapacity}
                                          onClick={() => {
                                            if (!isEnoughCapacity) return

                                            handleReserveCar(vehicle.id)
                                          }}
                                          className={`group grid w-full shrink-0 grid-cols-[68px_minmax(0,1fr)_72px] items-center gap-1.5 overflow-hidden rounded-[16px] border px-2 py-3 text-left transition-all duration-200 min-[375px]:grid-cols-[72px_minmax(0,1fr)_72px] min-[375px]:gap-2 min-[420px]:grid-cols-[100px_minmax(0,1fr)_88px] min-[420px]:gap-3 min-[420px]:px-3 min-[420px]:py-4 sm:min-h-[120px] sm:grid-cols-[160px_minmax(0,1fr)_116px] sm:px-4 lg:grid-cols-[120px_minmax(0,1fr)_90px] lg:px-3 lg:py-2 ${
                                            !isEnoughCapacity
                                              ? 'cursor-not-allowed border-[#E4E4E4] bg-[#F8F9FA] opacity-45 shadow-none hover:border-[#E4E4E4] hover:shadow-none'
                                              : 'border-[#D9D9D9] bg-white hover:border-[#9BC344] hover:shadow-[0_5px_15px_rgba(155,195,68,0.18)]'
                                          }`}
                                        >
                                          {/* Vehicle image */}
                                          <div className="flex h-[72px] w-full items-center justify-center min-[420px]:h-[90px] lg:h-[70px]">
                                            <img
                                              src={vehicle.image}
                                              alt={vehicle.name}
                                              className={`max-h-[62px] w-full object-contain min-[420px]:max-h-[78px] lg:max-h-[60px] ${
                                                !isEnoughCapacity
                                                  ? 'opacity-60'
                                                  : ''
                                              }`}
                                            />
                                          </div>

                                          {/* Vehicle information */}
                                          <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                              <p className="flex items-center gap-1.5 font-host text-[11px] font-medium text-[#222222]">
                                                <span
                                                  className={`h-[10px] w-[10px] shrink-0 rounded-full ${
                                                    !isEnoughCapacity
                                                      ? 'bg-[#B0B0B0]'
                                                      : 'bg-[#C7C7C7]'
                                                  }`}
                                                />

                                                {vehicle.category}
                                              </p>

                                              {!isEnoughCapacity && (
                                                <span className="rounded-full bg-[#FEE2E2] px-2 py-0.5 font-poppins text-[9px] font-semibold text-[#DC2626]">
                                                  Max {vehicle.passengers}{' '}
                                                  seats
                                                </span>
                                              )}
                                            </div>

                                            <h2
                                              className={`mt-1 truncate font-host text-[15px] font-semibold leading-tight sm:text-[18px] ${
                                                !isEnoughCapacity
                                                  ? 'text-[#555555]'
                                                  : 'text-black'
                                              }`}
                                            >
                                              {vehicle.name}
                                            </h2>

                                            <div className="mt-2 flex items-center gap-3 text-[#5D5D5D]">
                                              <span
                                                className={`flex items-center gap-1 font-host text-[12px] ${
                                                  !isEnoughCapacity
                                                    ? 'font-semibold text-[#DC2626]'
                                                    : ''
                                                }`}
                                              >
                                                <img
                                                  src="/assets/images/icon-adult.png"
                                                  alt=""
                                                  className={`h-[13px] w-[13px] object-contain ${
                                                    !isEnoughCapacity
                                                      ? 'opacity-90'
                                                      : 'opacity-70'
                                                  }`}
                                                />

                                                {vehicle.passengers}

                                                {!isEnoughCapacity
                                                  ? ' max'
                                                  : ''}
                                              </span>

                                              <span className="flex items-center gap-1 font-host text-[12px]">
                                                <img
                                                  src="/assets/images/icon-child.png"
                                                  alt=""
                                                  className="h-[13px] w-[13px] object-contain opacity-70"
                                                />

                                                {vehicle.luggage}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Vehicle price */}
                                          <div
                                            className={`flex min-h-[64px] flex-col items-center justify-center rounded-[13px] px-2 py-1.5 transition-colors duration-200 min-[420px]:min-h-[80px] ${
                                              !isEnoughCapacity
                                                ? 'bg-[#ECECEC] text-[#777777]'
                                                : 'bg-[#F5FBE7] group-hover:bg-[#EAF6D4]'
                                            }`}
                                          >
                                            <span
                                              className={`font-host text-[18px] font-semibold leading-none sm:text-[20px] ${
                                                !isEnoughCapacity
                                                  ? 'text-[#777777]'
                                                  : 'text-black'
                                              }`}
                                            >
                                              €{displayedPrice}
                                            </span>

                                            <span
                                              className={`mt-1 whitespace-nowrap font-poppins text-[9px] ${
                                                !isEnoughCapacity
                                                  ? 'font-semibold text-[#888888]'
                                                  : 'font-semibold text-[#365E92]'
                                              }`}
                                            >
                                              {!isEnoughCapacity
                                                ? 'Not enough seats'
                                                : 'Reserve Now ›'}
                                            </span>
                                          </div>
                                        </button>
                                      </div>
                                    )
                                  })}
                                </>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ================= TOUR OVERVIEW ================= */}
                    <section>
                      {/* Tabs */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveInfoTab('overview')}
                          className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                            activeInfoTab === 'overview'
                              ? 'border-slate-200 bg-slate-100 text-slate-800'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          Tour Overview
                        </button>

                        <button
                          type="button"
                          onClick={() => setActiveInfoTab('itinerary')}
                          className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                            activeInfoTab === 'itinerary'
                              ? 'border-brand-green bg-slate-100 text-slate-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          Itinerary
                        </button>
                      </div>

                      {/* Content */}
                      {activeInfoTab === 'overview' ? (
                        <p className="mt-4 font-host text-base leading-[1.65] text-[#4A4A4A] whitespace-pre-line">
                          {selectedTrip.overview ||
                            selectedTrip.shortDescription}
                        </p>
                      ) : (
                        <div className="mt-4 rounded-[12px] border border-slate-100 bg-[#F8FAFC] px-5 py-5 sm:px-6">
                          <h3 className="font-host text-[17px] font-bold text-[#111111]">
                            Itinerary
                          </h3>

                          <p className="mt-2 font-host text-[14px] sm:text-[15px] leading-[1.7] text-[#4A4A4A] whitespace-pre-line">
                            {selectedTrip.itinerary ||
                              'No itinerary available.'}
                          </p>
                        </div>
                      )}
                    </section>

                    {/* Trip Highlights */}
                    <section className="mt-10">
                      <h3 className="font-host text-[18px] font-bold text-[#111111]">
                        Trip Highlights
                      </h3>

                      <ul className="mt-4 space-y-3.5">
                        {selectedTrip.highlights &&
                          selectedTrip.highlights.map((tag, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <div className="w-2.5 h-2.5 rounded-full bg-[#FF7A29] flex-shrink-0" />

                              <span className="font-host text-[14px] sm:text-[15px] font-semibold text-[#3C3C3C]">
                                {tag}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </section>

                    {/* Excluded Features */}
                    {Boolean(
                      (selectedTrip.excludedFeatures &&
                        selectedTrip.excludedFeatures.length > 0) ||
                        (selectedTrip.excluded_features &&
                          selectedTrip.excluded_features.length > 0),
                    ) && (
                      <section className="mt-10">
                        <h3 className="font-host text-[18px] font-bold text-[#111111]">
                          Excluded Features
                        </h3>

                        <ul className="mt-4 space-y-3.5">
                          {(
                            selectedTrip.excludedFeatures ??
                            selectedTrip.excluded_features ??
                            []
                          ).map((item, idx) => (
                            <li
                              key={idx}
                              className="flex items-center gap-3"
                            >
                              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </div>

                              <span className="font-host text-[14px] sm:text-[15px] font-semibold text-[#555555]">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* What to Expect */}
                    <section className="mt-10">
                      <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800 mb-4">
                        What to Expect
                      </span>

                      <div className="space-y-6">
                        {expectations.map((exp, idx) => (
                          <div key={idx} className="flex gap-4">
                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange font-semibold text-[13px]">
                              {idx + 1}
                            </div>

                            <div>
                              <h4 className="font-host text-[15px] sm:text-[16px] font-bold text-[#111111]">
                                {exp.title}
                              </h4>

                              <p className="mt-1.5 font-host text-[13px] sm:text-[14px] leading-[1.5] text-[#555555]">
                                {exp.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* Good to Know */}
                    <section className="mt-10">
                      <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800 mb-4">
                        Good to Know
                      </span>

                      <ul className="space-y-3.5">
                        {selectedTrip.goodToKnow &&
                          selectedTrip.goodToKnow.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M13.3 4.3L6 11.6L2.7 8.3"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>

                              <span className="font-host text-[14px] font-semibold text-[#3C3C3C]">
                                {item}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </section>

                    {/* Map Section */}
                    <section className="mt-10">
                      <div className="w-full h-[360px] rounded-[24px] overflow-hidden border border-slate-200 shadow-sm">
                        <iframe
                          title={`Route Map to ${selectedTrip.title}`}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          src={`https://maps.google.com/maps?q=Salzburg%20to%20${encodeURIComponent(
                            selectedTrip.title,
                          )}&t=&z=10&ie=UTF8&iwloc=&output=embed`}
                          allowFullScreen
                          loading="lazy"
                        />
                      </div>
                    </section>
                  </div>

                  {/* ================= RIGHT COLUMN ================= */}
                  <div>
                    <div className="sticky top-6 flex flex-col gap-6">
                      <div className="p-6 rounded-[28px] border border-[#E5E5E5] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                        <div className="flex items-center justify-between">
                          <div className="hidden">
                            <div className="flex items-baseline gap-1">
                              <span className="font-host text-[26px] font-bold text-[#111111] leading-none">
                                From €{selectedTrip.price}
                              </span>
                            </div>

                            <div className="mt-1 font-host text-[12px] text-slate-500 font-medium flex items-center gap-1">
                              <span>per vehicle</span>

                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                className="text-slate-400"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line
                                  x1="12"
                                  y1="16"
                                  x2="12"
                                  y2="12"
                                />
                                <line
                                  x1="12"
                                  y1="8"
                                  x2="12.01"
                                  y2="8"
                                />
                              </svg>
                            </div>
                          </div>

                          <div className="ml-auto flex items-center gap-1.5 rounded-full bg-[#3b629b] px-4 py-2 text-[12px] font-semibold text-white">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <rect
                                x="3"
                                y="11"
                                width="18"
                                height="9"
                                rx="2"
                              />
                              <path d="M5 11V6a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v5" />
                              <circle
                                cx="7"
                                cy="15"
                                r="1.5"
                                fill="currentColor"
                              />
                              <circle
                                cx="17"
                                cy="15"
                                r="1.5"
                                fill="currentColor"
                              />
                            </svg>

                            <span>Private group ride</span>
                          </div>
                        </div>

                        <div className="mt-6 mb-3 flex items-center justify-between gap-3">
                          <h4 className="font-host text-[15px] font-semibold text-[#111111]">
                            Pickup Details & Travelers
                          </h4>

                          <div className="flex shrink-0 items-center gap-1.5">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="text-brand-green"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>

                            <span className="whitespace-nowrap font-host text-[13px] font-semibold text-[#444444]">
                              {selectedTrip.duration || '8–10 Hours'}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3">
                          {/* Location Selector */}
                          <div className="relative">
                            <div
                              className={`flex items-center justify-between border rounded-[16px] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer transition-all duration-200 ${
                                pickupLocationError
                                  ? 'border-red-500 bg-red-50/40 ring-2 ring-red-100'
                                  : 'border-[#EAEAEA] bg-white'
                              }`}
                              onClick={() => {
                                setIsFromOpen(!isFromOpen)
                                setIsDepartureOpen(false)
                                setShowTravelersDropdown(false)
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-host text-[12px] text-slate-500 font-medium">
                                  From Location
                                </span>

                                <span className="font-host text-[14px] font-bold text-black">
                                  {selectedFrom
                                    ? selectedFrom.title
                                    : 'Select Pickup Location'}
                                </span>
                              </div>

                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                className={`text-black transition-transform ${
                                  isFromOpen ? 'rotate-180' : ''
                                }`}
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>

                            {pickupLocationError && (
                              <p className="mt-1.5 px-1 font-host text-[12px] font-medium text-red-500">
                                Please select a pickup location.
                              </p>
                            )}

                            {isFromOpen && (
                              <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50">
                                <LocationDropdown
                                  onSelect={(place) => {
                                    setSelectedFrom({
                                      title: place,
                                      address: `${place}, Austria`,
                                    })

                                    setPickupLocationError(false)
                                    setIsFromOpen(false)
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Date and Time Selector */}
                          <div className="relative">
                            <div
                              className="flex items-center justify-between border border-[#EAEAEA] rounded-[16px] px-4 py-3 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer"
                              onClick={() => {
                                setIsDepartureOpen(!isDepartureOpen)
                                setIsFromOpen(false)
                                setShowTravelersDropdown(false)
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-host text-[12px] text-slate-500 font-medium">
                                  Date & Time
                                </span>

                                <span className="font-host text-[14px] font-bold text-black">
                                  {formatDateLabel(departure.date)} at{' '}
                                  {formatTimeLabel(departure)}
                                </span>
                              </div>

                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                className={`text-black transition-transform ${
                                  isDepartureOpen ? 'rotate-180' : ''
                                }`}
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>

                            {isDepartureOpen && (
                              <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50">
                                <DateTimePicker
                                  value={departure}
                                  allowNow
                                  onCancel={() =>
                                    setIsDepartureOpen(false)
                                  }
                                  onConfirm={(next) => {
                                    setDeparture(next)
                                    setIsDepartureOpen(false)
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Travelers Selector */}
                          <div className="relative">
                            <div
                              className="flex items-center justify-between border border-[#EAEAEA] rounded-[16px] px-4 py-3 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer"
                              onClick={() => {
                                setShowTravelersDropdown(
                                  !showTravelersDropdown,
                                )
                                setIsFromOpen(false)
                                setIsDepartureOpen(false)
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-host text-[12px] text-slate-500 font-medium">
                                  Travelers
                                </span>

                                <span className="font-host text-[14px] font-bold text-black">
                                  {travelersCount} Person
                                  {travelersCount > 1 ? 's' : ''}
                                </span>
                              </div>

                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                className={`text-black transition-transform ${
                                  showTravelersDropdown
                                    ? 'rotate-180'
                                    : ''
                                }`}
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>

                            {showTravelersDropdown && (
                              <>
                                <div
                                  className="fixed inset-0 z-30"
                                  onClick={() =>
                                    setShowTravelersDropdown(false)
                                  }
                                />

                                <div className="absolute right-0 mt-2 w-72 rounded-[16px] bg-white border border-slate-100 shadow-xl p-5 z-40">
                                  <div className="flex flex-col gap-6">
                                    {/* Adults row */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex flex-col">
                                        <span className="font-host text-[15px] font-bold text-black">
                                          Adults
                                        </span>

                                        <span className="font-host text-[12px] text-slate-400">
                                          Age 12+
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setAdultsCount(
                                              Math.max(
                                                1,
                                                adultsCount - 1,
                                              ),
                                            )
                                          }
                                          disabled={adultsCount <= 1}
                                          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E5E5E5] text-black hover:bg-[#D9D9D9] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                        >
                                          —
                                        </button>

                                        <span className="w-6 text-center font-host text-[15px] font-bold text-black">
                                          {adultsCount}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (
                                              adultsCount +
                                                childrenCount <
                                              8
                                            ) {
                                              setAdultsCount(
                                                adultsCount + 1,
                                              )
                                            }
                                          }}
                                          disabled={
                                            adultsCount +
                                              childrenCount >=
                                            8
                                          }
                                          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#BAD67E] text-black hover:bg-[#b0d26c] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>

                                    {/* Children row */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex flex-col">
                                        <span className="font-host text-[15px] font-bold text-black">
                                          Children
                                        </span>

                                        <span className="font-host text-[12px] text-slate-400">
                                          Age 0-12
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setChildrenCount(
                                              Math.max(
                                                0,
                                                childrenCount - 1,
                                              ),
                                            )
                                          }
                                          disabled={
                                            childrenCount <= 0
                                          }
                                          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E5E5E5] text-black hover:bg-[#D9D9D9] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                        >
                                          —
                                        </button>

                                        <span className="w-6 text-center font-host text-[15px] font-bold text-black">
                                          {childrenCount}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (
                                              adultsCount +
                                                childrenCount <
                                              8
                                            ) {
                                              setChildrenCount(
                                                childrenCount + 1,
                                              )
                                            }
                                          }}
                                          disabled={
                                            adultsCount +
                                              childrenCount >=
                                            8
                                          }
                                          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#BAD67E] text-black hover:bg-[#b0d26c] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Cancellation box */}
                        <div className="mt-4 flex gap-2.5 items-start border border-[#CCE5CC] rounded-[12px] bg-[#F7FDF7] p-3">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className="text-green-700 flex-shrink-0 mt-0.5"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>

                          <span className="font-host text-[13px] font-semibold text-green-800 leading-normal">
                            Free cancellation up to 24 hours before your
                            pickup time.
                          </span>
                        </div>

                        {/* Existing hidden total-price section from A */}
                        <div className="hidden mt-4 p-4 rounded-[16px] bg-[#F0F2F5] flex-col">
                          <span className="font-host text-[13px] font-medium text-slate-500">
                            {groupText}
                          </span>

                          <div className="flex items-center justify-between mt-1">
                            <span className="font-host text-[18px] font-bold text-black">
                              Total
                            </span>

                            <span className="font-host text-[24px] font-bold text-black">
                              €{calculatedPrice}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            setShowCars(true)

                            setTimeout(() => {
                              vehiclesRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              })
                            }, 100)

                            toast(
                              'Please select a vehicle below to proceed.',
                              {
                                icon: '⬇️',
                              },
                            )
                          }}
                          className="w-full mt-4 rounded-full bg-[#FF6A1A] hover:bg-[#E05B13] active:scale-[0.98] py-4 font-host text-[16px] font-bold text-white shadow-[0_4px_12px_rgba(255,106,26,0.15)] transition-all"
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-32">
                <p className="font-host text-red-500 font-semibold text-[15px]">
                  Failed to load trip details. Please try again.
                </p>

                <button
                  onClick={() => setSearchParams({})}
                  className="mt-4 rounded-full border border-slate-300 px-6 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Return to Listing
                </button>
              </div>
            )}
          </main>
        </div>
      ) : (
        /* ================= LISTING VIEW ================= */
        <div className="flex flex-1 flex-col">
          <div
            className="relative flex h-[420px] w-full items-center overflow-hidden bg-cover bg-center bg-no-repeat sm:h-[500px] lg:h-[540px]"
            style={{
              backgroundImage:
                "url('/assets/images/day-trips-hero-banner.png')",
            }}
          >
            <div className="pointer-events-none absolute left-0 right-0 top-0 z-0 h-[320px] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.82)_0%,rgba(255,255,255,0.58)_32%,rgba(255,255,255,0.20)_68%,rgba(255,255,255,0)_100%)]" />

            <div className="relative z-10 w-full">
              <Header absolute />
            </div>
          </div>

          <main className="flex flex-1 flex-col items-center pb-20">
            <RoutesDiagram />

            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#365E92] rounded-full animate-spin" />

                <p className="mt-4 font-host text-slate-500 text-[14px]">
                  Loading day trips...
                </p>
              </div>
            ) : (
              <DayTripGrid
                trips={trips}
                onSelectTrip={(trip) =>
                  setSearchParams({ id: trip.id })
                }
              />
            )}
          </main>
        </div>
      )}

      <Footer />
    </div>
  )
}
